import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import Constants from "common/Constants";
import { launchOnlineLibraryPlayer } from "bridge/common/CommonBridge";
import usexAPI from "store/customHooks/usexAPI";
import { callPost } from "utils/CommonApi";
import { xAPI_ID_VALUE } from "utils/xAPI_PATH";
import ExamPlanLiveQnaAlert from "./ExamPlanLiveQnaAlert";
import "./ExamPlanMissionStatus.scss";
import AlertConfirmPopupNew from "components/common/AlertConfirmPopupNew";
import { EXAM_PLAN_XAPI_CONTENT } from "./examPlanXapi";
import { NORMALIZE_GRAD_CD, toExamDvsnCd } from "components/mypage/myExamInfo/examConstants";

const MISSION_ICON_BY_TARGET = {
    EXAM_SPC_LEC: "exam-lecture",
    UNIT_TEST: "unit-master",
    AI_STD_ROOM: "ai-reading",
    AI_ENGM: "ai-english",
    AI_MATH: "ai-math",
    ALL_ZIP: "all-zip",
};
const MISSION_ROUTE_BY_TARGET = {
    EXAM_SPC_LEC: "/smart/middleStudySmart?UP_MENU_CD=SMART&MENU_CD=EXAM&FROM_CD=TPMN",
    UNIT_TEST: "/testzone?UP_MENU_CD=TESTL&MENU_CD=UNITT&FROM_CD=TPMN",
    AI_ENGM: "/aime?UP_MENU_CD=AISTUDY&MENU_CD=ENGM&FROM_CD=TPMN",
    AI_MATH: "/aime?UP_MENU_CD=AISTUDY&MENU_CD=MATH&FROM_CD=TPMN",
    ALL_ZIP: "/allZip?FROM_CD=TOTL",
    LIVE_QNA: "/liveQna",
};
const MISSION_VISIT_MENU_BY_TARGET = {
    AI_ENGM: "ENGM",
    AI_MATH: "MATH",
    ALL_ZIP: "ALLZIP",
};
const MISSION_XAPI_ID_BY_TARGET = {
    EXAM_SPC_LEC: xAPI_ID_VALUE.serviceUnit.btn_epm_0002,
    UNIT_TEST: xAPI_ID_VALUE.serviceUnit.btn_epm_0003,
    AI_STD_ROOM: xAPI_ID_VALUE.serviceUnit.btn_epm_0004,
    AI_ENGM: xAPI_ID_VALUE.serviceUnit.btn_epm_0005,
    AI_MATH: xAPI_ID_VALUE.serviceUnit.btn_epm_0006,
    ALL_ZIP: xAPI_ID_VALUE.serviceUnit.btn_epm_0007,
    LIVE_QNA: xAPI_ID_VALUE.serviceUnit.btn_epm_0008,
};
const EMPTY_MISSIONS = [];

const isLiveQnaOperating = (schedule) => {
    if (!schedule) return false;

    const serverTime = moment(schedule.SERVER_TM);
    if (!serverTime.isValid()) return false;

    const currentDate = serverTime.format("YYYY-MM-DD");
    const currentTime = serverTime.format("HH:mm");
    const day = serverTime.day();

    return (
        day >= 1 &&
        day <= 4 &&
        currentDate >= schedule.RLTM_INQ_START_DT &&
        currentDate <= schedule.RLTM_INQ_END_DT &&
        currentTime >= schedule.RLTM_INQ_START_TM &&
        currentTime <= schedule.RLTM_INQ_END_TM
    );
};

function ExamPlanMissionStatus({
    missionStatusList,
    missionStatusTotal,
    todayMissionHistoryList,
    missionStatusData,
    onNavigate,
    onVisit,
    onScholarReview,
    isAiStudyRoomActive,
    isMissionExpired = false,
}) {
    const { ServiceUnit_B } = usexAPI();
    const userId = useSelector((state) => state.loginReducer.userId);
    const gradCd = useSelector((state) => state.loginReducer.gradCd);
    const mmbrSeq = useSelector((state) => state.loginReducer.mmbrSeq);
    const contractNumber = useSelector((state) => state.loginReducer.contractNumber);
    const customerNumber = useSelector((state) => state.loginReducer.customerNumber);
    const subscriberType = useSelector((state) => state.loginReducer.subscriberType);
    const pedcCd = useSelector((state) => state.loginReducer.pedcCd);
    const [liveQnaSchedule, setLiveQnaSchedule] = useState(null);
    const [isLiveQnaAlertOpen, setIsLiveQnaAlertOpen] = useState(false);
    const [, setIsLiveQnaActive] = useState(false);
    const [isRealTimeQnaModalOpen, setIsRealTimeQnaModalOpen] = useState(false);
    const isAllMissionsComplete =
        Number(missionStatusTotal?.TOTAL_MISSION_CNT) > 0 &&
        Number(missionStatusTotal?.COMPLETE_MISSION_CNT) >= Number(missionStatusTotal?.TOTAL_MISSION_CNT);
    const isFinalExam =missionStatusTotal?.EXAM_DVSN_CD_NM === "기말고사";
    /** 중3 2학기 기말고사 기간에는 바로답변을 운영하지 않는다 (사업부 요청) */
    const isLiveQnaClosedExam =
        NORMALIZE_GRAD_CD(missionStatusData?.MIS_PLAN_INFO?.GRAD_CD || gradCd) === "GR23" &&
        toExamDvsnCd(missionStatusData?.MIS_PLAN_INFO) === "2F";
    /** 사업부 구분(subscriberType) : K 교문 / M 미래 / P 플랫폼 */
    const isFutureSubscriber = String(subscriberType).toUpperCase() === "M";
    const todayMissionSeqs = useMemo(
        () =>
            new Set(
                (Array.isArray(todayMissionHistoryList) ? todayMissionHistoryList : []).map((history) =>
                    String(history?.PLAN_MISN_SEQ ?? history),
                ),
            ),
        [todayMissionHistoryList],
    );
    const missions = useMemo(() => {
        if (!Array.isArray(missionStatusList)) return EMPTY_MISSIONS;

        return missionStatusList
            .filter((mission) => MISSION_ICON_BY_TARGET[mission.MISSION_TARGET_CD])
            .sort((missionA, missionB) => missionA.SORT_ORD - missionB.SORT_ORD)
            .map((mission) => ({
                ...mission,
                icon: MISSION_ICON_BY_TARGET[mission.MISSION_TARGET_CD],
                missionNumber: String(mission.SORT_ORD).padStart(2, "0"),
                progressPercent: Math.max(0, Math.min(100, Number(mission.COMP_PERCENT) || 0)),
            }));
    }, [missionStatusList]);

    const handleOnlineLibraryOpen = useCallback(() => {
        launchOnlineLibraryPlayer();

        callPost(
            {
                CONTRACT_NUMBER: contractNumber,
                CUSTOMER_NUMBER: customerNumber,
            },
            {
                REQUEST_API: `${Constants.BRCS_API.BRCS_ENTER}?PEDC_CD=${pedcCd}`,
                HTTP_METHOD: Constants.HTTP_METHOD.PUT,
                BODY: {
                    BRCS_HST_DVSN_CD: "BS",
                    ACMT_STD_TM: "0",
                },
            },
            null,
            { MMBR_SEQ: mmbrSeq },
        );
    }, [contractNumber, customerNumber, mmbrSeq, pedcCd]);

    const handleMissionMove = useCallback(
        (missionTargetCode, menuCode, upMenuCode, xapiId = MISSION_XAPI_ID_BY_TARGET[missionTargetCode]) => {
            if (xapiId) ServiceUnit_B(xapiId).setContent(EXAM_PLAN_XAPI_CONTENT).selected();

            if (missionTargetCode === "AI_STD_ROOM") {
                if (onVisit) onVisit(menuCode);
                handleOnlineLibraryOpen();
                return;
            }

            const route = MISSION_ROUTE_BY_TARGET[missionTargetCode];
            if (!route || !onNavigate) return;

            if (missionTargetCode === "AI_ENGM") {
                localStorage.setItem("aiEngNav", "exam");
            } else if (missionTargetCode === "AI_MATH") {
                localStorage.setItem("aiMathTab", 4);
            }

            onNavigate(route, MISSION_VISIT_MENU_BY_TARGET[missionTargetCode], upMenuCode);
        },
        [ServiceUnit_B, handleOnlineLibraryOpen, onNavigate, onVisit],
    );
    const loadLiveQnaSchedule = useCallback(
        (openAlert = false) => {
            callPost(
                { user_id: userId },
                {
                    REQUEST_API: Constants.LIVEQNA_API.USE_SERVICE_INFO,
                    HTTP_METHOD: Constants.HTTP_METHOD.GET,
                },
                (res) => {
                    const responseData = res?.RESP_RESULT?.RESP_RESULT || res?.RESP_RESULT;
                    const schedule = responseData?.STD_RLTM_INQ || null;

                    setLiveQnaSchedule(schedule);
                    setIsLiveQnaActive(isLiveQnaOperating(schedule));
                    if (openAlert) setIsLiveQnaAlertOpen(true);
                },
                { GRAD_CD: gradCd },
            );
        },
        [gradCd, userId],
    );

    useEffect(() => {
        loadLiveQnaSchedule();
        const timer = setInterval(loadLiveQnaSchedule, 60000);

        return () => clearInterval(timer);
    }, [loadLiveQnaSchedule]);

    return (
        <section className="expln-mssn-list">
            <ul className="expln-mssn-list__items">
                {/* 미션을 모두 완료했거나, 시험대비모드 기간이 끝나 종료 사실을 알려야 할 때 노출 */}
                {(isAllMissionsComplete || isMissionExpired) && (
                    <li className="expln-mssn-list__item is-scholar">
                        <div className="expln-mssn-list__scholar-head">
                            <div className="expln-mssn-list__scholar-title">
                                <strong>
                                    {isFinalExam
                                        ? "기말고사 성적을 입력하고 성적 우수 장학생에 도전해 보세요!"
                                        : "기말고사에는 성적 우수 장학생에 도전해 보세요!"}
                                </strong>
                                <p>
                                    {isFinalExam &&
                                        (missionStatusData?.CTGR_EVENT_INFO?.SCSH_DSP_START_DT &&
                                        missionStatusData?.CTGR_EVENT_INFO?.SCSH_DSP_END_DT
                                            ? `${missionStatusData.CTGR_EVENT_INFO.SCSH_DSP_START_DT} ~ ${missionStatusData.CTGR_EVENT_INFO.SCSH_DSP_END_DT}`
                                            : "이벤트 준비중입니다.")}
                                </p>
                            </div>

                            <button
                                className="expln-mssn-list__go"
                                type="button"
                                onClick={() => {
                                    ServiceUnit_B(xAPI_ID_VALUE.serviceUnit.btn_epm_0014)
                                        .setContent(EXAM_PLAN_XAPI_CONTENT)
                                        .selected();

                                    // 미래 사업부(subscriberType: M) 회원은 성적 우수 장학생 이벤트 대상이 아니라 진입을 막는다.
                                    if (isFutureSubscriber) {
                                        AlertConfirmPopupNew({
                                            type: "ALERT",
                                            msg: "<p>해당 이용권은<br /> 접근이 불가능합니다.</p>",
                                        });
                                        return;
                                    }

                                    if (isFinalExam) onNavigate("/reward");
                                    else onScholarReview();
                                }}
                            />
                        </div>
                        <div className="expln-mssn-list__scholar-table">
                            <table>
                                <colgroup>
                                    <col />
                                    <col />
                                    <col />
                                    <col />
                                </colgroup>
                                <tbody>
                                    <tr>
                                        <td>올백상</td>
                                        <td>주요과목 100점</td>
                                        <td>성적 향상상</td>
                                        <td>평균 5점 또는 10점 이상 향상</td>
                                    </tr>
                                    <tr>
                                        <td>최우수상</td>
                                        <td>주요과목 평균 98점 이상</td>
                                        <td>영수 만점상</td>
                                        <td>영서, 수학 모두 100점</td>
                                    </tr>
                                    <tr>
                                        <td>올백상</td>
                                        <td>주요과목 평균 95점 이상</td>
                                        <td colSpan={2} />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p className="expln-mssn-list__scholar-note">*주요과목 : 국/영/수/사/역/과</p>
                    </li>
                )}

                {missions.map((mission) => (
                    <li
                        key={mission.PLAN_MISN_SEQ}
                        className={`expln-mssn-list__item ${mission.COMPLETE_YN === "Y" ? "is-complete" : ""}`}
                    >
                        <div className="expln-mssn-list__box">
                            <span className={`expln-mssn-list__icon expln-mssn-list__icon--${mission.icon}`}>
                                {mission.COMPLETE_YN === "Y" && <span className="expln-mssn-list__done" />}
                            </span>
                            <div className="expln-mssn-list__tit">
                                <span className="expln-mssn-list__tag">MISSION {mission.missionNumber}</span>
                                <span className="expln-mssn-list__name">{mission.TARGET_NM}</span>
                            </div>
                            <div className="expln-progress">
                                {todayMissionSeqs.has(String(mission.PLAN_MISN_SEQ)) && (
                                    <div className="expln-progress__complete">오늘 완료</div>
                                )}
                                <div className="expln-progress__bar">
                                    <span
                                        className="expln-progress__gauge"
                                        style={{ width: `${mission.progressPercent}%` }}
                                    />
                                </div>
                                <span className="expln-progress__count">
                                    {mission.PROGRESS_COUNT} /{mission.MISSION_COUNT}
                                </span>
                            </div>
                        </div>
                        <button
                            className="expln-mssn-list__go"
                            type="button"
                            disabled={
                                isMissionExpired ||
                                (mission.MISSION_TARGET_CD === "AI_STD_ROOM" && !isAiStudyRoomActive)
                            }
                            onClick={() =>
                                handleMissionMove(mission.MISSION_TARGET_CD, mission.MENU_CD, mission.UP_MENU_CD)
                            }
                        />
                    </li>
                ))}

                <li className="expln-mssn-list__item is-live">
                    <div className="expln-mssn-list__box">
                        <span className="expln-mssn-list__icon expln-mssn-list__icon--live-answer" />
                        <div className="expln-mssn-list__tit">
                            <div className="expln-mssn-list__name">
                                실시간 바로 답변
                                <button
                                    type="button"
                                    className="expln-mssn-list__info"
                                    onClick={() => loadLiveQnaSchedule(true)}
                                />
                            </div>
                        </div>
                    </div>
                    <button
                        className="expln-mssn-list__go"
                        type="button"
                        disabled={isMissionExpired}
                        onClick={() => handleMissionMove("LIVE_QNA")}
                    />
                </li>
            </ul>
            <ExamPlanLiveQnaAlert
                isOpen={isLiveQnaAlertOpen}
                schedule={liveQnaSchedule}
                isClosedExam={isLiveQnaClosedExam}
                onClose={() => setIsLiveQnaAlertOpen(false)}
            />
        </section>
    );
}

export default ExamPlanMissionStatus;
